import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useState } from 'react';
import { employees as api, vehicles as vehiclesApi } from '@/api/endpoints';
import type {
  Timesheet, WorkTimeEntry, WorkTimeEntryInput, EmployeeMeta, DayType,
  Vehicle, EmployeePayroll, EmployeePayrollInput, MonthlySettlement,
} from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { formatCurrency } from '@/lib/money';

const MIES = ['styczeń', 'luty', 'marzec', 'kwiecień', 'maj', 'czerwiec',
  'lipiec', 'sierpień', 'wrzesień', 'październik', 'listopad', 'grudzień'];

const hhmm = (t?: string | null) => (t ? t.slice(0, 5) : '');
const pad2 = (n: number) => String(n).padStart(2, '0');

function openDownload(url: string) {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Zakładka „Ewidencja / Nadgodziny" na stronie pracownika: miesięczny rejestr godzin, podsumowanie
 * czasu (norma/nadgodziny) oraz PODSUMOWANIE płacowe (tylko OWNER/ADMIN). Eksport XLSX/PDF, wypełnianie
 * z kalendarza i zatwierdzanie miesiąca.
 */
export function EwidencjaTab({ employeeId, canEdit, meta }: {
  employeeId: number;
  canEdit: boolean;
  meta: EmployeeMeta | null;
}) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [ts, setTs] = useState<Timesheet | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [vehiclesList, setVehiclesList] = useState<Vehicle[]>([]);
  const [entryModal, setEntryModal] = useState<WorkTimeEntry | 'new' | null>(null);
  const [payrollOpen, setPayrollOpen] = useState(false);
  const [settleOpen, setSettleOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const reload = () => api.timesheet(employeeId, year, month).then(setTs).catch(e => setErr(e?.message ?? String(e)));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [employeeId, year, month]);
  useEffect(() => { vehiclesApi.list().then(setVehiclesList).catch(() => {}); }, []);

  const prevMonth = () => { if (month === 1) { setYear(year - 1); setMonth(12); } else setMonth(month - 1); };
  const nextMonth = () => { if (month === 12) { setYear(year + 1); setMonth(1); } else setMonth(month + 1); };

  if (err) return <div className="text-red-500 text-[13px]">Błąd: {err}</div>;
  if (!ts) return <div className="text-ink-400">Ładowanie…</div>;

  const s = ts.settlement;
  const finalized = s.status === 'FINALIZED';
  const firstDay = `${year}-${pad2(month)}-01`;
  const lastDayStr = `${year}-${pad2(month)}-${pad2(new Date(year, month, 0).getDate())}`;
  const hasMoney = s.hourlyPay != null;   // serwer redaguje kwoty dla OFFICE

  const onPrefill = async () => {
    if (!confirm('Wypełnić ewidencję z zaplanowanych wyjazdów tego miesiąca? Dni już wpisane zostaną pominięte.')) return;
    try { const r = await api.prefillFromCalendar(employeeId, firstDay, lastDayStr); await reload(); alert(`Dodano ${r.created} dni z kalendarza.`); }
    catch (e) { alert('Błąd: ' + (e instanceof Error ? e.message : String(e))); }
  };
  const onDelete = async (w: WorkTimeEntry) => {
    if (!confirm(`Usunąć wpis z ${w.workDate}?`)) return;
    try { await api.workTimeEntries.remove(employeeId, w.id); reload(); }
    catch (e) { alert('Błąd: ' + (e instanceof Error ? e.message : String(e))); }
  };
  const onFinalize = async () => {
    if (!confirm('Zatwierdzić miesiąc? Zostanie zamrożony, a pozostałe saldo przejdzie na kolejny miesiąc.')) return;
    setBusy(true);
    try { await api.settlement(employeeId, year, month, { normHours: s.normHours, paidCash: s.paidCash ?? undefined }, true); reload(); }
    catch (e) { alert('Błąd: ' + (e instanceof Error ? e.message : String(e))); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid gap-4">
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={prevMonth} className="btn btn-ghost btn-icon" title="Poprzedni miesiąc"><Icon name="chevron" className="w-4 h-4 rotate-180"/></button>
        <div className="font-semibold text-ink-900 min-w-[150px] text-center capitalize">{MIES[month - 1]} {year}</div>
        <button onClick={nextMonth} className="btn btn-ghost btn-icon" title="Następny miesiąc"><Icon name="chevron" className="w-4 h-4"/></button>
        <span className="pill" style={{ background: finalized ? '#dcfce7' : '#fef3c7', color: finalized ? '#15803d' : '#b45309' }}>{s.statusName}</span>
        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          {canEdit && !finalized && <button onClick={onPrefill} className="btn btn-secondary btn-sm"><Icon name="calendar" className="w-3.5 h-3.5"/> Wypełnij z kalendarza</button>}
          {hasMoney && <button onClick={() => openDownload(api.timesheetXlsxUrl(employeeId, year, month))} className="btn btn-ghost btn-sm"><Icon name="download" className="w-3.5 h-3.5"/> XLSX</button>}
          {hasMoney && <button onClick={() => openDownload(api.timesheetPdfUrl(employeeId, year, month))} className="btn btn-ghost btn-sm"><Icon name="download" className="w-3.5 h-3.5"/> PDF</button>}
          {canEdit && <button onClick={() => setPayrollOpen(true)} className="btn btn-ghost btn-sm"><Icon name="settings" className="w-3.5 h-3.5"/> Stawki</button>}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Stat label="Norma" value={`${s.normHours} h`}/>
        <Stat label="Przepracowane" value={`${s.workedHours} h`}/>
        <Stat label="Nadgodziny" value={`${s.overtimeHours} h`} accent={Number(s.overtimeHours) > 0}/>
        <Stat label="Święta/niedziele" value={`${s.holidayHours} h`}/>
        <Stat label="Delegacje" value={`${s.delegationCount}`}/>
      </div>

      <div className="card"><div className="card-body !p-0">
        <div className="flex items-center px-4 py-2 border-b border-ink-100">
          <span className="text-[11px] uppercase tracking-wider font-semibold text-ink-500">Wpisy ({ts.entries.length})</span>
          {canEdit && !finalized && <button onClick={() => setEntryModal('new')} className="ml-auto btn btn-secondary btn-sm"><Icon name="plus" className="w-3.5 h-3.5"/> Dodaj dzień</button>}
        </div>
        {ts.entries.length === 0 ? <div className="p-5 text-center text-ink-400 text-[12px]">Brak wpisów w tym miesiącu.</div> : (
          <table className="table-hw">
            <thead><tr><th className="w-28">Data</th><th className="w-24">Typ</th><th className="w-28">Od–Do</th><th className="w-16">Godz.</th><th className="w-16">Deleg.</th><th>Lokalizacja</th><th className="w-28">Pojazd</th><th className="w-16"></th></tr></thead>
            <tbody>
              {ts.entries.map(w => (
                <tr key={w.id}>
                  <td className="font-mono text-[12px]">{w.workDate}</td>
                  <td>{w.dayType !== 'WORK'
                    ? <span className="pill" style={{ background: '#eef2ff' }}>{w.dayTypeName}</span>
                    : <span className="text-ink-400 text-[11px]">Praca</span>}</td>
                  <td className="font-mono text-[12px]">{hhmm(w.startTime)}{w.endTime ? '–' + hhmm(w.endTime) : ''}</td>
                  <td className="font-mono text-[12px]">{w.hoursWorked}</td>
                  <td className="text-[12px]">{Number(w.delegationValue) > 0 ? w.delegationValue : '—'}</td>
                  <td className="text-[12px]">{w.location || '—'}</td>
                  <td className="text-[12px]">{w.vehicleLabel || '—'}</td>
                  <td>{canEdit && !finalized && <div className="flex gap-1 justify-end">
                    <button onClick={() => setEntryModal(w)} className="btn btn-ghost btn-icon text-ink-400 hover:text-water-600"><Icon name="edit" className="w-3.5 h-3.5"/></button>
                    <button onClick={() => onDelete(w)} className="btn btn-ghost btn-icon text-ink-400 hover:text-red-500"><Icon name="x" className="w-3.5 h-3.5"/></button>
                  </div>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div></div>

      {hasMoney && (
        <div className="card"><div className="card-body">
          <div className="flex items-center mb-3">
            <div className="card-title">PODSUMOWANIE — {MIES[month - 1]} {year}</div>
            {canEdit && !finalized && (
              <div className="ml-auto flex gap-2">
                <button onClick={() => setSettleOpen(true)} className="btn btn-ghost btn-sm"><Icon name="edit" className="w-3.5 h-3.5"/> Edytuj</button>
                <button onClick={onFinalize} disabled={busy} className="btn btn-primary btn-sm"><Icon name="check" className="w-3.5 h-3.5"/> Zatwierdź miesiąc</button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 text-[13px]">
            <Money k="Ubezpieczenie" v={s.insurance}/>
            <Money k="Karta lunchowa" v={s.lunchCard}/>
            <Money k="Delegacje" v={s.delegationPay}/>
            <Money k="Godziny pracy" v={s.hourlyPay}/>
            <Money k="Dodatek za nadgodziny" v={s.overtimePremiumPay}/>
            <Money k="Podstawa wg umowy" v={s.baseSalary}/>
            <Money k="Do wypłaty gotówką" v={s.cashTopUp} bold/>
            <Money k="Saldo z poprz. miesiąca" v={s.carryoverIn}/>
            <Money k="Wypłacono gotówką" v={s.paidCash}/>
            <Money k="Pozostałe saldo (→ następny m-c)" v={s.carryoverOut} bold/>
          </div>
        </div></div>
      )}

      {entryModal && meta && (
        <EntryModal employeeId={employeeId} item={entryModal === 'new' ? null : entryModal} meta={meta}
          vehicles={vehiclesList} defaultDate={firstDay}
          onClose={() => setEntryModal(null)} onSaved={() => { setEntryModal(null); reload(); }}/>
      )}
      {payrollOpen && (
        <PayrollModal employeeId={employeeId} payroll={ts.payroll}
          onClose={() => setPayrollOpen(false)} onSaved={() => { setPayrollOpen(false); reload(); }}/>
      )}
      {settleOpen && (
        <SettlementModal employeeId={employeeId} year={year} month={month} settlement={s}
          onClose={() => setSettleOpen(false)} onSaved={() => { setSettleOpen(false); reload(); }}/>
      )}
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="rounded-md border border-ink-100 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">{label}</div>
      <div className={'text-[16px] font-semibold font-mono ' + (accent ? 'text-amber-600' : 'text-ink-900')}>{value}</div>
    </div>
  );
}

function Money({ k, v, bold }: { k: string; v?: number | null; bold?: boolean }) {
  return (
    <div className={'flex items-center justify-between border-b border-ink-100/70 py-1 ' + (bold ? 'font-semibold text-ink-900' : 'text-ink-600')}>
      <span>{k}</span><span className="font-mono">{formatCurrency(v)}</span>
    </div>
  );
}

// ── Wspólna otoczka modala (lokalna, by uniknąć importu cyklicznego) ──────────
function Shell({ title, onClose, onSubmit, busy, err, children, draftValue }: {
  title: string; onClose: () => void; onSubmit: (e: React.FormEvent) => void;
  busy: boolean; err: string | null; children: React.ReactNode; draftValue: unknown;
}) {
  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: draftValue, onClose, busy,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <form onClick={e => e.stopPropagation()} onSubmit={onSubmit}
            className="bg-white rounded-lg shadow-xl w-full max-w-[620px] max-h-[92vh] overflow-auto">
        <div className="px-5 py-3 border-b border-ink-100"><h2 className="text-[15px] font-semibold text-ink-900">{title}</h2></div>
        <div className="p-5 grid gap-3">
          {children}
          {err && <div className="text-red-600 text-[12px] p-2 bg-red-50 border border-red-200 rounded">{err}</div>}
          <div className="flex justify-end gap-2 mt-1">
            <button type="button" onClick={requestClose} disabled={busy} className="btn btn-secondary">Anuluj</button>
            <button type="submit" disabled={busy} className="btn btn-primary"><Icon name="check" className="w-3.5 h-3.5"/> {busy ? 'Zapisywanie…' : 'Zapisz'}</button>
          </div>
        </div>
      </form>
    </div>
  );
}

function EntryModal({ employeeId, item, meta, vehicles, defaultDate, onClose, onSaved }: {
  employeeId: number; item: WorkTimeEntry | null; meta: EmployeeMeta; vehicles: Vehicle[];
  defaultDate: string; onClose: () => void; onSaved: () => void;
}) {
  const isEdit = !!item;
  const [workDate, setWorkDate] = useState(item?.workDate ?? defaultDate);
  const [dayType, setDayType] = useState<DayType>(item?.dayType ?? 'WORK');
  const [startTime, setStartTime] = useState(hhmm(item?.startTime) || '07:00');
  const [endTime, setEndTime] = useState(hhmm(item?.endTime) || '15:00');
  const [delegationValue, setDelegationValue] = useState<string>(item?.delegationValue != null ? String(item.delegationValue) : '0');
  const [vehicleId, setVehicleId] = useState<number | ''>(item?.vehicleId ?? '');
  const [location, setLocation] = useState(item?.location ?? '');
  const [notes, setNotes] = useState(item?.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const isWork = dayType === 'WORK';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isWork && endTime < startTime) { setErr('Godzina zakończenia nie może być wcześniejsza niż rozpoczęcia'); return; }
    setBusy(true); setErr(null);
    const payload: WorkTimeEntryInput = {
      workDate, dayType,
      startTime: isWork ? startTime : null,
      endTime: isWork ? endTime : null,
      delegationValue: Number(delegationValue),
      vehicleId: vehicleId === '' ? null : Number(vehicleId),
      location: location.trim() || null,
      notes: notes.trim() || null,
    };
    try {
      if (isEdit) await api.workTimeEntries.update(employeeId, item!.id, payload);
      else await api.workTimeEntries.add(employeeId, payload);
      onSaved();
    } catch (er) { setErr(er instanceof Error ? er.message : String(er)); setBusy(false); }
  };

  return (
    <Shell title={isEdit ? 'Edytuj dzień' : 'Dodaj dzień pracy'} onClose={onClose} onSubmit={submit} busy={busy} err={err} draftValue={{ workDate, dayType, startTime, endTime, delegationValue, vehicleId, location, notes }}>
      <div className="grid grid-cols-2 gap-3">
        <label className="field"><span className="field-label">Data *</span>
          <input type="date" value={workDate} onChange={e => setWorkDate(e.target.value)} className="input"/></label>
        <label className="field"><span className="field-label">Typ dnia</span>
          <SearchableSelect value={dayType} onChange={v => setDayType(v as DayType)}>
            {meta.dayTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </SearchableSelect></label>
      </div>
      {isWork && (
        <div className="grid grid-cols-3 gap-3">
          <label className="field"><span className="field-label">Od *</span>
            <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} className="input"/></label>
          <label className="field"><span className="field-label">Do *</span>
            <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} className="input"/></label>
          <label className="field"><span className="field-label">Delegacja</span>
            <SearchableSelect value={delegationValue} onChange={setDelegationValue}>
              <option value="0">brak</option>
              <option value="0.5">0,5</option>
              <option value="1">1</option>
            </SearchableSelect></label>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <label className="field"><span className="field-label">Lokalizacja</span>
          <input value={location} onChange={e => setLocation(e.target.value)} className="input" placeholder="np. Stargard / baza / UW"/></label>
        <label className="field"><span className="field-label">Pojazd</span>
          <SearchableSelect value={vehicleId === '' ? '' : String(vehicleId)} onChange={v => setVehicleId(v ? Number(v) : '')}>
            <option value="">— brak —</option>
            {vehicles.map(v => <option key={v.id} value={v.id}>{v.label}{v.registrationNumber ? ` (${v.registrationNumber})` : ''}</option>)}
          </SearchableSelect></label>
      </div>
      <label className="field"><span className="field-label">Uwagi</span>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} className="textarea" rows={2}/></label>
    </Shell>
  );
}

function PayrollModal({ employeeId, payroll, onClose, onSaved }: {
  employeeId: number; payroll: EmployeePayroll; onClose: () => void; onSaved: () => void;
}) {
  const [hourlyRate, setHourlyRate] = useState(String(payroll.hourlyRate ?? 46));
  const [delegationRate, setDelegationRate] = useState(String(payroll.delegationRate ?? 45));
  const [lunchCardMonthly, setLunchCardMonthly] = useState(String(payroll.lunchCardMonthly ?? 0));
  const [insuranceMonthly, setInsuranceMonthly] = useState(String(payroll.insuranceMonthly ?? 0));
  const [overtimePct, setOvertimePct] = useState(String(payroll.overtimePct ?? 50));
  const [sundayHolidayPct, setSundayHolidayPct] = useState(String(payroll.sundayHolidayPct ?? 100));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    const data: EmployeePayrollInput = {
      hourlyRate: Number(hourlyRate), delegationRate: Number(delegationRate),
      lunchCardMonthly: Number(lunchCardMonthly), insuranceMonthly: Number(insuranceMonthly),
      overtimePct: Number(overtimePct), sundayHolidayPct: Number(sundayHolidayPct),
    };
    try { await api.payroll.update(employeeId, data); onSaved(); }
    catch (er) { setErr(er instanceof Error ? er.message : String(er)); setBusy(false); }
  };

  const F = (label: string, v: string, set: (s: string) => void, suffix: string) => (
    <label className="field"><span className="field-label">{label}</span>
      <div className="flex items-center gap-1">
        <input type="number" step="0.01" min="0" value={v} onChange={e => set(e.target.value)} className="input"/>
        <span className="text-[12px] text-ink-400 whitespace-nowrap">{suffix}</span>
      </div>
    </label>
  );

  return (
    <Shell title="Parametry płacowe" onClose={onClose} onSubmit={submit} busy={busy} err={err} draftValue={{ hourlyRate, delegationRate, lunchCardMonthly, insuranceMonthly, overtimePct, sundayHolidayPct }}>
      <div className="grid grid-cols-2 gap-3">
        {F('Stawka godzinowa', hourlyRate, setHourlyRate, 'zł/h')}
        {F('Stawka delegacji', delegationRate, setDelegationRate, 'zł')}
        {F('Karta lunchowa (mies.)', lunchCardMonthly, setLunchCardMonthly, 'zł')}
        {F('Ubezpieczenie (mies.)', insuranceMonthly, setInsuranceMonthly, 'zł')}
        {F('Dodatek nadgodziny', overtimePct, setOvertimePct, '%')}
        {F('Dodatek niedz./święta', sundayHolidayPct, setSundayHolidayPct, '%')}
      </div>
    </Shell>
  );
}

function SettlementModal({ employeeId, year, month, settlement, onClose, onSaved }: {
  employeeId: number; year: number; month: number; settlement: MonthlySettlement; onClose: () => void; onSaved: () => void;
}) {
  const [normHours, setNormHours] = useState(String(settlement.normHours ?? ''));
  const [paidCash, setPaidCash] = useState(settlement.paidCash != null ? String(settlement.paidCash) : '');
  const [notes, setNotes] = useState(settlement.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await api.settlement(employeeId, year, month, {
        normHours: normHours === '' ? null : Number(normHours),
        paidCash: paidCash === '' ? null : Number(paidCash),
        notes: notes.trim() || null,
      }, false);
      onSaved();
    } catch (er) { setErr(er instanceof Error ? er.message : String(er)); setBusy(false); }
  };

  return (
    <Shell title="Edytuj rozliczenie miesiąca" onClose={onClose} onSubmit={submit} busy={busy} err={err} draftValue={{ normHours, paidCash, notes }}>
      <div className="grid grid-cols-2 gap-3">
        <label className="field"><span className="field-label">Norma (godz.)</span>
          <input type="number" step="0.01" value={normHours} onChange={e => setNormHours(e.target.value)} className="input"/></label>
        <label className="field"><span className="field-label">Wypłacono gotówką (zł)</span>
          <input type="number" step="0.01" value={paidCash} onChange={e => setPaidCash(e.target.value)} className="input"/></label>
      </div>
      <label className="field"><span className="field-label">Notatka</span>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} className="textarea" rows={2}/></label>
      <div className="text-[11px] text-ink-400 -mt-1">
        „Wypłacono gotówką" mniejsze niż należna kwota → różnica przejdzie na kolejny miesiąc jako saldo (po zatwierdzeniu).
      </div>
    </Shell>
  );
}
